/* ==============================================================================
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
============================================================================== */

import { useState, useEffect } from 'react';

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types';

// @material-ui core components
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDBadge from 'components/MDBadge';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import Breadcrumbs from 'examples/Breadcrumbs';
import NotificationItem from 'examples/Items/NotificationItem';
import { APP_STATE } from 'services/Define';
import { useSelector, useDispatch } from 'react-redux';
import { setLang } from 'redux/actions/appAction';
import { useTranslation } from 'react-i18next';

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from 'examples/Navbars/DashboardNavbar/styles';

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from 'context';

async function notifyUser(notificationText = 'Thank you for enable') {
  if (!('Notification' in window)) {
    alert('Browser does not support notifications');
  } else if (Notification.permission === 'granted') {
    const notification = new Notification(notificationText);
  } else if (Notification.permission !== 'denined') {
    await Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        const notification = new Notification(notificationText);
      }
    });
  }
}

function Navbar({ absolute, light, isMini, route }) {
  const app = useSelector((state) => state[APP_STATE]);
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const [navbarType, setNavbarType] = useState();
  const [controller, controllerDispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [userResponded, setUserResponded] = useState(false);

  useEffect(() => {
    // console.log(isMini);
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType('sticky');
    } else {
      setNavbarType('static');
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(
        controllerDispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener('scroll', handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener('scroll', handleTransparentNavbar);
  }, [controllerDispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(controllerDispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(controllerDispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  // Handle language change
  const handleLanguageChange = (lang) => {
    dispatch(setLang(lang));
    i18n.changeLanguage(lang);
  };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      {app.notify.data ||
        app.notify.data.map((item) => (
          <NotificationItem
            key={item.id}
            url={`/message/${item.id}`}
            icon={<span>{item.date_message}</span>}
            title={item.title}
          />
        ))}
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  const enableNotiAndClose = async () => {
    await notifyUser().then(() => {
      setUserResponded(true);
    });
  };

  const disableNotiAndClose = () => {
    setUserResponded(true);
  };

  return (
    <AppBar
      position={absolute ? 'absolute' : navbarType}
      color='inherit'
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color='inherit' mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs
            icon='home'
            title={route[route.length - 1]}
            route={route}
            light={light}
            isMini={isMini}
            home={app.home}
          />
          <IconButton sx={navbarDesktopMenu} onClick={handleMiniSidenav} size='small' disableRipple>
            <Icon fontSize='medium' sx={iconsStyle}>
              {miniSidenav ? 'menu_open' : 'menu'}
            </Icon>
          </IconButton>
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDTypography
              sx={{ display: { xs: 'block', sm: 'none' } }}
              fontWeight='bold'
              textTransform='capitalize'
              variant='h5'
              color={light ? 'white' : 'dark'}
              noWrap
              pt={2}
            >
              {route[route.length - 1]}
              <IconButton
                size='small'
                disableRipple
                color='inherit'
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize='medium'>
                  {miniSidenav ? 'menu_open' : 'menu'}
                </Icon>
              </IconButton>
            </MDTypography>
            <MDBox color={light ? 'white' : 'inherit'} display='flex' alignItems='center' gap={1}>
              {/* Language Toggle */}
              <MDBox display='flex' alignItems='center' gap={0.5}>
                <IconButton
                  size='small'
                  disableRipple
                  color='inherit'
                  sx={{
                    ...navbarIconButton,
                    backgroundColor: app.lang === 'th' ? 'rgba(255,255,255,0.1)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                  }}
                  onClick={() => handleLanguageChange('th')}
                >
                  <MDTypography
                    variant='button'
                    fontWeight='medium'
                    sx={{
                      fontSize: '0.75rem',
                      color: app.lang === 'th' ? (light ? 'white' : 'primary.main') : 'inherit',
                    }}
                  >
                    TH
                  </MDTypography>
                </IconButton>
                <MDTypography variant='caption' sx={iconsStyle}>
                  |
                </MDTypography>
                <IconButton
                  size='small'
                  disableRipple
                  color='inherit'
                  sx={{
                    ...navbarIconButton,
                    backgroundColor: app.lang === 'en' ? 'rgba(255,255,255,0.1)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                  }}
                  onClick={() => handleLanguageChange('en')}
                >
                  <MDTypography
                    variant='button'
                    fontWeight='medium'
                    sx={{
                      fontSize: '0.75rem',
                      color: app.lang === 'en' ? (light ? 'white' : 'primary.main') : 'inherit',
                    }}
                  >
                    EN
                  </MDTypography>
                </IconButton>
              </MDBox>

              {/* Notifications */}
              {app.notify.count > 0 ? (
                <MDBox>
                  {!userResponded && !(Notification.permission === 'granted') ? (
                    <IconButton
                      size='small'
                      disableRipple
                      color='inherit'
                      sx={navbarIconButton}
                      onClick={enableNotiAndClose}
                    >
                      <Icon color='error'>notifications_active</Icon>
                    </IconButton>
                  ) : null}
                  <IconButton
                    size='small'
                    disableRipple
                    color='inherit'
                    sx={navbarIconButton}
                    aria-controls='notification-menu'
                    aria-haspopup='true'
                    variant='contained'
                    onClick={handleOpenMenu}
                  >
                    <MDBadge badgeContent={app.notify.count} color='error' size='xs' circular>
                      <Icon sx={iconsStyle}>mail_outline</Icon>
                    </MDBadge>
                  </IconButton>
                  {/* {renderMenu()} */}
                </MDBox>
              ) : null}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
Navbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  route: ['collapse', 'page'],
};

// Typechecking props for the DashboardNavbar
Navbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  route: PropTypes.arrayOf(PropTypes.string),
};

export default Navbar;
